import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import DropdownToggle from './DropdownToggle';
import OptionsList from './OptionsList';
import SelectedPills from './SelectedPills';

const LIST_OFFSET_PX = 4;
const LIST_MAX_HEIGHT_PX = 280;
const VIEWPORT_PADDING_PX = 12;

/**
 * MultiSelectField
 *
 * A fully accessible, controlled multi-select dropdown that:
 *  • Shows a button (combobox) summarising the selection count
 *  • Opens a list of checkboxes for picking/unpicking options
 *  • Renders the chosen items as Pills below, with a “+N more” toggle
 *  • Persists selection to localStorage (optional)
 *  • Supports keyboard navigation (Arrow keys, Enter/Space, Esc)
 *
 * Props:
 * @param {string} label
 *    — Placeholder text used when nothing is selected.
 *    — Also shown in assistive text as “Select {label}”.
 *
 * @param {{ id: string, name: string }[]} options
 *    — Array of objects representing the available options.
 *    — `id` is the unique key, `name` is the human-readable label.
 *
 * @param {string[]} value
 *    — Array of `id`s for currently selected options.
 *    — Controlled from the parent component.
 *
 * @param {(newIds: string[]) => void} onChange
 *    — Callback invoked with the updated array of selected IDs.
 *
 * @param {boolean} [disabled=false]
 *    — When true: disables the combobox toggle, prevents opening.
 *
 * @param {number} [maxVisible=5]
 *    — Maximum number of Pills to display before collapsing the rest
 *      into a “+N more” toggle.
 *
 * Usage (controlled):
 *    const [selected, setSelected] = useState([]);
 *    <MultiSelectField
 *      label="Facilities"
 *      options={facilityOptions}
 *      value={selected}
 *      onChange={setSelected}
 *      maxVisible={3}
 *      persistKey="demoFacilities"
 *      disabled={false}
 *    />
 */
const MultiSelectField = ({ label, options, value, onChange, disabled, maxVisible }) => {
  const [open, setOpen] = useState(false);
  const [listPosition, setListPosition] = useState(null);
  const [highlightedIndex, setHiIndex] = useState(-1);
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef(null);
  const toggleRef = useRef(null);
  const listRef = useRef(null);

  // Position list in viewport: below or above toggle depending on space; cap height to avoid overflow
  useLayoutEffect(() => {
    if (!open) {
      setListPosition(null);
      return;
    }
    const el = toggleRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - LIST_OFFSET_PX - VIEWPORT_PADDING_PX;
    const spaceAbove = rect.top - VIEWPORT_PADDING_PX;
    const openDown = spaceBelow >= spaceAbove;
    const maxHeight = Math.min(
      LIST_MAX_HEIGHT_PX,
      openDown ? spaceBelow : spaceAbove,
    );
    const top = openDown
      ? rect.bottom + LIST_OFFSET_PX
      : rect.top - maxHeight - LIST_OFFSET_PX;
    setListPosition({
      top,
      left: rect.left,
      width: rect.width,
      maxHeight,
    });
  }, [open]);

  // Outside click to close (portal list is outside containerRef, so check both)
  useEffect(() => {
    const handler = (e) => {
      const inContainer = containerRef.current?.contains(e.target);
      const inList = listRef.current?.contains(e.target);
      if (!inContainer && !inList) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Toggle one option
  const toggleOption = useCallback(
    (id) => {
      const idx = value.indexOf(id);
      const next = idx === -1 ? [...value, id] : value.filter((v) => v !== id);
      onChange(next);
    },
    [value, onChange],
  );

  // Keyboard navigation
  const onKeyDown = useCallback(
    (e) => {
      if (!open) return;
      const max = options.length - 1;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHiIndex((i) => Math.min(i + 1, max));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHiIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (highlightedIndex >= 0) {
            toggleOption(options[highlightedIndex].id);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
        default:
          break;
      }
    },
    [open, highlightedIndex, options, toggleOption],
  );

  const listStyle =
    listPosition &&
    ({
      position: 'fixed',
      top: listPosition.top,
      left: listPosition.left,
      width: listPosition.width,
      minWidth: listPosition.width,
      maxHeight: listPosition.maxHeight,
    });

  return (
    <div className="relative" ref={containerRef} onKeyDown={onKeyDown}>
      <DropdownToggle
        ref={toggleRef}
        open={open}
        disabled={disabled}
        count={value.length}
        label={label}
        onToggle={() => setOpen((o) => !o)}
      />
      {open &&
        listPosition &&
        createPortal(
          <div ref={listRef} data-multiselect-list aria-hidden="false">
            <OptionsList
              options={options}
              selectedIds={value}
              highlightedIndex={highlightedIndex}
              onToggle={toggleOption}
              onHover={setHiIndex}
              listStyle={listStyle}
            />
          </div>,
          document.body,
        )}
      <SelectedPills
        options={options}
        selectedIds={value}
        maxVisible={maxVisible}
        expanded={expanded}
        onToggleExpand={() => setExpanded((x) => !x)}
      />
    </div>
  );
};

MultiSelectField.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  maxVisible: PropTypes.number,
  persistKey: PropTypes.string,
};

export default memo(MultiSelectField);
